import { useEffect, useState } from "react";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import { SEOHead } from "@/components/SEOHead";
import { MapPin } from "lucide-react";

export default function Contact() {
  const [loadHeavy, setLoadHeavy] = useState(false);

  useEffect(() => {
    const handleInteraction = () => {
      setLoadHeavy(true);
      cleanup();
    };

    const cleanup = () => {
      window.removeEventListener('scroll', handleInteraction);
      window.removeEventListener('mousemove', handleInteraction);
      window.removeEventListener('touchstart', handleInteraction);
      window.removeEventListener('keydown', handleInteraction);
    };

    window.addEventListener('scroll', handleInteraction, { passive: true });
    window.addEventListener('mousemove', handleInteraction, { passive: true });
    window.addEventListener('touchstart', handleInteraction, { passive: true });
    window.addEventListener('keydown', handleInteraction, { passive: true });

    const timer = setTimeout(handleInteraction, 3500);

    return () => {
      cleanup();
      clearTimeout(timer);
    };
  }, []);

  useEffect(() => {
    if (!loadHeavy) return;
    const script = document.createElement("script");
    script.src = "https://link.velocesolutions.net/js/form_embed.js";
    script.async = true;
    document.body.appendChild(script);
    
    return () => {
      if (document.body.contains(script)) {
        document.body.removeChild(script);
      }
    };
  }, [loadHeavy]);

  return (
    <div className="min-h-screen bg-background font-sans text-foreground flex flex-col">
      <SEOHead 
        title="Contact | Woods Spine & Wellness in Longview, TX"
        description="Contact Woods Spine and Wellness in Longview, TX. Schedule your appointment for SoftWave TRT, Red Light Therapy, or DRX9000 Spinal Decompression."
        ogImage="https://vibe.filesafe.space/1774569058151009111/assets/58600117-1663-43e4-98fd-7b421e93ecd3.png"
        canonical="https://woodsspine.com/contact"
      />
      <Navbar />
      <main id="main-content" className="flex-grow">
        <section className="py-16 bg-muted/30 border-b border-border">
          <div className="container mx-auto px-4 text-center">
            <h1 className="text-4xl md:text-5xl font-bold text-primary mb-4">
              Contact <span className="text-accent">Woods Spine and Wellness</span>
            </h1>
          </div>
        </section>
        
        <section className="py-16">
          <div className="container mx-auto px-4 max-w-6xl">
            <div className="grid lg:grid-cols-2 gap-16 mb-16">
              {/* Contact Info */}
              <div className="space-y-10">
                <div>
                  <h3 className="text-xl font-bold text-primary mb-2 border-b pb-2">Visit our Location</h3>
                  <p className="text-muted-foreground mt-4 hover:text-primary transition-colors">
                    <a href="https://www.google.com/maps?cid=8246813296068272509" target="_blank" rel="noopener noreferrer">
                      530 E Loop 281, Suite 103<br/>Longview, TX 75605
                    </a>
                  </p>
                </div>

                <div>
                  <h3 className="text-xl font-bold text-primary mb-2 border-b pb-2">Give us a Call</h3>
                  <p className="text-muted-foreground mt-4">(903) 215-8828</p>
                </div>
                
                <div>
                  <h3 className="text-xl font-bold text-primary mb-2 border-b pb-2">Send us a Message</h3>
                  <p className="text-muted-foreground mt-4">
                    <a href="sms:+19032158828" className="text-accent hover:underline">TEXT</a> or <a href="mailto:woodsspine.office@gmail.com" className="text-accent hover:underline">EMAIL</a>
                  </p>
                </div>

                <div>
                  <h3 className="text-xl font-bold text-primary mb-2 border-b pb-2">Office Hours</h3>
                  <ul className="text-muted-foreground space-y-2 mt-4 text-sm">
                    <li>Monday: 8:30AM - 12:30PM & 2PM - 6PM</li>
                    <li>Tuesday: 8:30AM - 12:30PM & 2PM - 6PM</li>
                    <li>Wednesday: 8:30AM - 12:30PM & 2PM - 6PM</li>
                    <li>Thursday: 8:30AM - 12:30PM & 2PM - 5PM</li>
                    <li>Friday: CLOSED</li>
                    <li>Saturday & Sunday: CLOSED</li>
                  </ul>
                </div>
              </div>

              {/* Contact Form */}
              <div className="bg-card text-card-foreground rounded-2xl p-8 shadow-xl border border-border h-fit min-h-[850px] relative flex flex-col">
                <h2 className="text-3xl font-bold text-primary mb-8 text-center">Send us a Message</h2>
                {!loadHeavy ? (
                  <div className="absolute inset-0 flex flex-col items-center justify-center text-muted-foreground bg-muted/50 rounded-2xl mt-20">
                    <div className="w-10 h-10 border-4 border-primary border-t-transparent rounded-full animate-spin mb-4"></div>
                    <p>Loading contact form...</p>
                  </div>
                ) : (
                  <iframe
                    src="https://link.velocesolutions.net/widget/form/0uG0uEn2GVWDbH2tS8W1"
                    style={{ width: "100%", height: "100%", border: "none", borderRadius: "4px", flexGrow: 1 }}
                    id="inline-0uG0uEn2GVWDbH2tS8W1" 
                    data-layout="{'id':'INLINE'}"
                    data-trigger-type="alwaysShow"
                    data-trigger-value=""
                    data-activation-type="alwaysActivated"
                    data-activation-value=""
                    data-deactivation-type="neverDeactivate"
                    data-deactivation-value=""
                    data-form-name="Contact Us"
                    data-height="821"
                    data-layout-iframe-id="inline-0uG0uEn2GVWDbH2tS8W1"
                    data-form-id="0uG0uEn2GVWDbH2tS8W1"
                    title="Contact Us"
                  />
                )}
              </div>
            </div>

            {/* Map */}
            <div className="w-full h-[450px] rounded-xl overflow-hidden shadow-lg border border-border relative bg-muted">
              {!loadHeavy ? (
                <div className="absolute inset-0 flex flex-col items-center justify-center text-muted-foreground">
                  <MapPin className="h-10 w-10 mb-2 opacity-50 animate-pulse" />
                  <p>Loading map...</p>
                </div>
              ) : (
                <iframe 
                  src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3363.500840582648!2d-94.7292483!3d32.5394761!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x86363fe9b171deb3%3A0x72729ff23a5ca37d!2sWoods%20Spine%20%26%20Wellness!5e0!3m2!1sen!2sus!4v1774571953004!5m2!1sen!2sus" 
                  width="100%" 
                  height="100%" 
                  style={{ border: 0 }} 
                  allowFullScreen 
                  loading="lazy" 
                  referrerPolicy="no-referrer-when-downgrade"
                  className="absolute inset-0"
                ></iframe>
              )}
            </div>
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
}
